import { useCallback, useEffect, useState } from "react";
import organizationalStructureApi, {
  ORGANIZATIONAL_STRUCTURE_TYPES,
} from "../services/organizationalStructureApi";

const TYPE_TO_STATE_KEY = {
  [ORGANIZATIONAL_STRUCTURE_TYPES.units]: "units",
  [ORGANIZATIONAL_STRUCTURE_TYPES.departments]: "departments",
  [ORGANIZATIONAL_STRUCTURE_TYPES.branches]: "branches",
  [ORGANIZATIONAL_STRUCTURE_TYPES.categories]: "categories",
};

const INITIAL_STATE = {
  units: [],
  departments: [],
  branches: [],
  categories: [],
};

export default function useOrganizationalStructureManagement() {
  const [data, setData] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [units, departments, branches, categories] = await Promise.all([
        organizationalStructureApi.getUnits(),
        organizationalStructureApi.getDepartments(),
        organizationalStructureApi.getBranches(),
        organizationalStructureApi.getCategories(),
      ]);

      setData({ units, departments, branches, categories });
    } catch (requestError) {
      setError(requestError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const saveItem = useCallback(async (type, formData, editingItem) => {
    if (editingItem?._id) {
      const updatedItem = await organizationalStructureApi.updateItem(
        type,
        editingItem._id,
        formData,
      );

      setData((current) => ({
        ...current,
        [TYPE_TO_STATE_KEY[type]]: current[TYPE_TO_STATE_KEY[type]].map(
          (item) => (item._id === editingItem._id ? updatedItem : item),
        ),
      }));

      return updatedItem;
    }

    const createdItem = await organizationalStructureApi.createItem(
      type,
      formData,
    );

    setData((current) => ({
      ...current,
      [TYPE_TO_STATE_KEY[type]]: [
        ...current[TYPE_TO_STATE_KEY[type]],
        createdItem,
      ],
    }));

    return createdItem;
  }, []);

  const removeItem = useCallback(async (type, id) => {
    const deletedResult = await organizationalStructureApi.deleteItem(type, id);

    setData((current) => ({
      ...current,
      [TYPE_TO_STATE_KEY[type]]: current[TYPE_TO_STATE_KEY[type]].filter(
        (item) => item._id !== id,
      ),
    }));

    return deletedResult;
  }, []);

  return {
    ...data,
    error,
    loading,
    fetchAllData,
    saveItem,
    removeItem,
  };
}
